import type { AuthStateUpdate, SetGlobalDataCache } from 'App/AppContextProvider';
import CurrentUser from 'models/currentuser';
import { IdentityProviderType } from 'models/identityProvider';
import Login from 'models/login';
import Tenant from 'models/tenant';
import User from 'models/user';
import UserLanguage from 'models/userLanguage';
import { HomeAccount } from 'models/userSetting';
import { apiGetTenantLogo, apiUpdateTenant } from 'services/Api/tenantService';
import { hasRolesFeatureGenericManager } from 'services/Auth/featurePermissions';
import Config from 'services/Config/configService';
import logger from 'services/Logging/logService';
import { getBrowserTimeZoneCode } from 'utils/datetime';
import { overflow } from 'utils/string';

//
// The provider-neutral tail of a sign-in: everything after the back-end login answered and the current
// tenant and user are known. Moved out of authService.setUserProfile as it was, so that a second identity
// provider runs the same steps with its own token and its own source of the user's photo. Throws on
// failure; the caller's catch signs the user out.
//
export const completeLoginProfile = async (
  login: Login,
  currentTenant: Tenant,
  currentUser: User,
  accessTokenApi: string,
  getUserPhoto: () => Promise<Blob | undefined>,
  authStateUpdate: AuthStateUpdate,
  setGlobalDataCache: SetGlobalDataCache,
): Promise<void> => {
  //create new user object
  const usr = new CurrentUser(
    currentUser.id,
    currentTenant,
    currentUser.name,
    currentUser.email,
    login.userLanguageCode || UserLanguage.getBrowserLanguage(),
  );

  usr.login = login;
  usr.identityProviderType = login.identityProviderType;

  //getting photo
  usr.photo = await getUserPhoto();

  //when user has logged into an organizational unit, update the tenant name
  if (currentTenant.parentId && currentTenant.topLevelParentTenantId !== currentTenant.id) {
    logger.debug('Logged into organizational unit', currentTenant);
    const topLevelParent = login.tenants?.find((ou) => ou.id === currentTenant?.topLevelParentTenantId);
    logger.debug('Top level tenant', topLevelParent);
    usr.login.isOrgUnit = true;
    if (topLevelParent) {
      usr.tenant.name = overflow(topLevelParent.name, 48) + ' | ' + overflow(currentTenant.name, 48);
    }
  } else {
    usr.tenant.name = overflow(currentTenant.name, 100);
  }

  logger.debug(`Final tenant name: ${usr.tenant.name}`);

  //determine whether user is guest based on home account setting. The home account is an Entra tenant id and
  //"guest" is an Entra notion: the setting is keyed on the email, so a Google user whose email also has a
  //Microsoft home account must not read as a guest here
  const homeAccount = usr.login.globalDataCache.userSettings.get(HomeAccount) as string;
  logger.debug('Home account set: ' + (homeAccount ?? 'unknown'));
  if (homeAccount && login.identityProviderType === IdentityProviderType.Entra) {
    usr.login.isGuest = homeAccount.toLowerCase() !== usr.tenant.providerTenantId?.toLowerCase();
  }
  logger.debug(`User is guest: ${usr.login.isGuest}`);

  //impersonation
  if (usr.id !== usr.login.userId && usr.login.userId && usr.id) {
    logger.debug('SUPPORT request');
    // back-end reports another user-id. can be the case when impersonating a user for support
    const supportUser = usr.login.globalDataCache.users.get(usr.login.userId);
    usr.name = 'SUPPORT ' + supportUser.name;
  }

  //set the global data cache we got from the login api call and clear the temp cache on the login object
  logger.debug('Init global data cache');
  setGlobalDataCache(usr.login.globalDataCache);
  usr.login.globalDataCache.clear();

  //User is authenticated
  //NOTE: THIS MUST BE DONE BEFORE ANY API CALLS ARE MADE SO THAT THE CORRECT ORG UNIT IS USED
  authStateUpdate(true, usr, false, undefined);

  //A tenant without a time zone adopts the one the browser reports, so that dates the server renders
  //(notifications, generated task instances, the public API) land on the user's wall clock instead of on
  //the neutral default. It is done here rather than on the settings page because most tenants never open
  //it. The API maps the zone onto a TimeZone row, so the stored code is one the picker lists.
  //Only a manager may write it: PUT /tenant is behind the same four roles as FeatureTypes.GenericManager.
  if (
    usr.login.userLicensed === true &&
    usr.login.tenantLicensed === true &&
    !usr.tenant.timeZoneCode &&
    hasRolesFeatureGenericManager(usr.login.userSystemRoles)
  ) {
    try {
      const browserTimeZoneCode = getBrowserTimeZoneCode();
      if (browserTimeZoneCode) {
        logger.debug('Stamp tenant time zone', browserTimeZoneCode);

        //A minimal DTO rather than usr.tenant, which is the live object and carries fields the endpoint
        //acts on. subscriptionType is copied from it deliberately: the endpoint changes the subscription
        //of a trial tenant when the posted value differs, so it has to match to stay a no-op.
        const tenantToStamp = new Tenant(usr.tenant.id, usr.tenant.name);
        tenantToStamp.parentId = usr.tenant.parentId;
        tenantToStamp.topLevelParentTenantId = usr.tenant.topLevelParentTenantId;
        tenantToStamp.subscriptionType = usr.tenant.subscriptionType;
        tenantToStamp.timeZoneCode = browserTimeZoneCode;

        //The org unit header is not set yet on a cold load, so the id travels explicitly; without it the
        //request resolves to the top level tenant and the API refuses the mismatched id with a 400.
        const savedTenant = await apiUpdateTenant(
          tenantToStamp,
          accessTokenApi,
          usr.login.isOrgUnit ? login.tenantId : undefined,
        );

        //Take the stored code back: the API normalises it, and every later reader of the tenant in this
        //session would otherwise still see it as unset.
        usr.tenant.timeZoneCode = savedTenant.timeZoneCode;
      }
    } catch (err) {
      //Never fail the sign-in over this: the enclosing catch signs the user out.
      logger.debug('Error stamping tenant time zone', err);
    }
  }

  //get the logo
  if (usr.login.userLicensed === true) {
    logger.debug('Get custom logo');
    usr.tenant.appLogo = await apiGetTenantLogo(accessTokenApi, usr.login.isOrgUnit ? login.tenantId : undefined);
    if (!usr.tenant.appLogo) {
      const logoFile = 'nav-bar-isoplanner.png';
      try {
        usr.tenant.appLogo = await fetch(`${Config.getImageURL()}/${logoFile}`).then((r) => r.blob());
      } catch (err) {
        logger.debug('Error getting default logo', err);
      }
    }
  }
};
